// All dimensions internally stored in INCHES (PDF/canvas base unit).
// Physical badge insert size purchased for the conference: 60 × 90 mm.
export type PageSize = "letter" | "a4";
export type Orientation = "portrait" | "landscape";
export type LayoutMode = "letter" | "a4" | "avery";

export const MM_PER_IN = 25.4;
export const mm = (n: number) => n / MM_PER_IN;

/** Physical badge insert size (millimetres). */
export const BADGE_MM = { w: 60, h: 90 };
export const BADGE_IN = { w: mm(BADGE_MM.w), h: mm(BADGE_MM.h) }; // 2.3622 × 3.5433

/** Internal safe margin — no important text within 3 mm of any edge. */
export const SAFE_MARGIN_MM = 3;
export const SAFE_MARGIN_IN = mm(SAFE_MARGIN_MM);

export interface AveryTemplate {
  id: string;
  label: string;
  badgeW: number;   // inches
  badgeH: number;   // inches
  pageSize: PageSize;
  orientation: Orientation;
  cols: number;
  rows: number;
  marginTop: number;   // inches
  marginLeft: number;  // inches
  colGap: number;      // inches
  rowGap: number;      // inches
}

export const PAGE_DIMS: Record<PageSize, { w: number; h: number }> = {
  letter: { w: 8.5, h: 11 },
  a4: { w: mm(210), h: mm(297) },
};

export interface LayoutOptions {
  pageSize: PageSize;
  orientation: Orientation;
  /** Horizontal gap between badges, mm. */
  gapH: number;
  /** Vertical gap between badges, mm. */
  gapV: number;
  /** Force a specific column count (Avery). Auto if undefined. */
  cols?: number;
  /** Force a specific row count (Avery). Auto if undefined. */
  rows?: number;
  /** Label / id override. */
  id?: string;
  label?: string;
}

/** Build a badge layout that tiles the exact 60×90 mm insert size,
 *  centered on the chosen page with equal margins. */
export function buildBadgeLayout(opts: LayoutOptions): AveryTemplate {
  const size = PAGE_DIMS[opts.pageSize];
  const pageW = opts.orientation === "landscape" ? size.h : size.w;
  const pageH = opts.orientation === "landscape" ? size.w : size.h;
  const gapH = mm(Math.max(0, opts.gapH));
  const gapV = mm(Math.max(0, opts.gapV));

  const maxCols = Math.max(1, Math.floor((pageW + gapH) / (BADGE_IN.w + gapH)));
  const maxRows = Math.max(1, Math.floor((pageH + gapV) / (BADGE_IN.h + gapV)));
  const cols = Math.max(1, Math.min(opts.cols ?? maxCols, maxCols));
  const rows = Math.max(1, Math.min(opts.rows ?? maxRows, maxRows));

  const usedW = cols * BADGE_IN.w + (cols - 1) * gapH;
  const usedH = rows * BADGE_IN.h + (rows - 1) * gapV;
  const marginLeft = Math.max(0, (pageW - usedW) / 2);
  const marginTop = Math.max(0, (pageH - usedH) / 2);

  return {
    id: opts.id ?? `${opts.pageSize}-${opts.orientation}-${opts.gapH}x${opts.gapV}`,
    label: opts.label ?? `${opts.pageSize.toUpperCase()} ${opts.orientation} · ${cols}×${rows} · gap ${opts.gapH}/${opts.gapV} mm`,
    badgeW: BADGE_IN.w,
    badgeH: BADGE_IN.h,
    pageSize: opts.pageSize,
    orientation: opts.orientation,
    cols,
    rows,
    marginTop,
    marginLeft,
    colGap: gapH,
    rowGap: gapV,
  };
}

/** Default preset list surfaced in dropdowns.
 *  Users can still customise gaps live via the Settings panel. */
export const AVERY_TEMPLATES: AveryTemplate[] = [
  buildBadgeLayout({ pageSize: "letter", orientation: "portrait", gapH: 5, gapV: 5, id: "letter-portrait-5mm", label: "Letter · Portrait · 5 mm gap" }),
  buildBadgeLayout({ pageSize: "letter", orientation: "portrait", gapH: 0, gapV: 0, id: "letter-portrait-nogap", label: "Letter · Portrait · No gap (max badges)" }),
  buildBadgeLayout({ pageSize: "a4", orientation: "portrait", gapH: 5, gapV: 5, id: "a4-portrait-5mm", label: "A4 · Portrait · 5 mm gap" }),
  buildBadgeLayout({ pageSize: "a4", orientation: "portrait", gapH: 0, gapV: 0, id: "a4-portrait-nogap", label: "A4 · Portrait · No gap (max badges)" }),
  // Avery-compatible: uses Letter with typical Avery margins for name-badge sheets.
  buildBadgeLayout({ pageSize: "letter", orientation: "portrait", gapH: 3, gapV: 0, id: "avery-badge-letter", label: "Avery-compatible · Letter · 6/sheet" }),
];

export const getTemplate = (id: string) =>
  AVERY_TEMPLATES.find((t) => t.id === id) ?? AVERY_TEMPLATES[0];

export function perPage(t: AveryTemplate) {
  return t.cols * t.rows;
}

// ─────────────────────────────────────────────────────────────────────────────
// Print calibration store (per-printer). Compensates for printer drift so the
// printed rectangle actually measures 60 × 90 mm inside the plastic holder.
// ─────────────────────────────────────────────────────────────────────────────

export interface PrinterCalibration {
  /** User-friendly printer label (HP, Brother, Canon, Epson office, etc.). */
  printer: string;
  /** Horizontal size scale factor, e.g. 1.02 = enlarge width by 2 %. */
  scaleX: number;
  /** Vertical size scale factor. */
  scaleY: number;
  /** Shift the whole grid right by N mm (negative = left). */
  offsetX: number;
  /** Shift the whole grid down by N mm (negative = up). */
  offsetY: number;
  updatedAt: number;
}

const CALIB_STORAGE_KEY = "ofuesi.badgeCalibration.v1";
const ACTIVE_PRINTER_KEY = "ofuesi.badgeCalibration.activePrinter";

export const DEFAULT_CALIBRATION: PrinterCalibration = {
  printer: "Default",
  scaleX: 1, scaleY: 1,
  offsetX: 0, offsetY: 0,
  updatedAt: 0,
};

export function loadCalibrations(): Record<string, PrinterCalibration> {
  try {
    const raw = localStorage.getItem(CALIB_STORAGE_KEY);
    if (!raw) return { Default: { ...DEFAULT_CALIBRATION } };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { Default: { ...DEFAULT_CALIBRATION } };
    if (!parsed.Default) parsed.Default = { ...DEFAULT_CALIBRATION };
    return parsed;
  } catch {
    return { Default: { ...DEFAULT_CALIBRATION } };
  }
}

export function saveCalibration(c: PrinterCalibration) {
  const all = loadCalibrations();
  all[c.printer] = { ...c, updatedAt: Date.now() };
  try { localStorage.setItem(CALIB_STORAGE_KEY, JSON.stringify(all)); } catch { /* noop */ }
}

export function deleteCalibration(printer: string) {
  const all = loadCalibrations();
  delete all[printer];
  try { localStorage.setItem(CALIB_STORAGE_KEY, JSON.stringify(all)); } catch { /* noop */ }
}

export function getActivePrinter(): string {
  try { return localStorage.getItem(ACTIVE_PRINTER_KEY) || "Default"; } catch { return "Default"; }
}

export function setActivePrinter(name: string) {
  try { localStorage.setItem(ACTIVE_PRINTER_KEY, name); } catch { /* noop */ }
}

export function getActiveCalibration(): PrinterCalibration {
  const all = loadCalibrations();
  return all[getActivePrinter()] || all.Default || { ...DEFAULT_CALIBRATION };
}
